import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { normalizeRole } from "../../utils/roleMap";
import { clearStoredAuth, getStoredIdentity } from "../../utils/authSession";
import {
  extractPlatformAccessBlock,
  getMyPlatformAccessStatus,
} from "../../services/platformAccessApi";
import {
  clearStoredPlatformAccessBlock,
  storePlatformAccessBlock,
} from "../../utils/platformAccessClientState";

const roleHome = {
  admin: "/dashboard/admin",
  applicant: "/dashboard/applicant",
  employer: "/dashboard/employer",
  press: "/dashboard/press",
};

const PLATFORM_ACCESS_POLL_MS = 5000;

function RoleGateContent({ role, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const redirectLockRef = useRef(false);

  useEffect(() => {
    let active = true;

    const handleBlocked = (decision) => {
      if (redirectLockRef.current) return;
      redirectLockRef.current = true;
      storePlatformAccessBlock({
        ...decision,
        redirectedFrom: `${location.pathname}${location.search}`,
      });
      clearStoredAuth();
      navigate("/login?platform_access=blocked&reason=session_access", {
        replace: true,
        state: { platformAccessBlocked: decision },
      });
    };

    const runCheck = async () => {
      try {
        await getMyPlatformAccessStatus();
        if (active) {
          setIsCheckingAccess(false);
          clearStoredPlatformAccessBlock();
        }
      } catch (error) {
        const block = extractPlatformAccessBlock(error);
        if (block) {
          handleBlocked(block);
          return;
        }
        if (error?.response?.status === 401) {
          clearStoredAuth();
          navigate("/login", { replace: true });
          return;
        }
        if (active) {
          setIsCheckingAccess(false);
        }
      }
    };

    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const block = extractPlatformAccessBlock(error);
        if (block) {
          handleBlocked(block);
        }
        return Promise.reject(error);
      }
    );

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        runCheck();
      }
    };

    const intervalId = window.setInterval(runCheck, PLATFORM_ACCESS_POLL_MS);
    window.addEventListener("focus", runCheck);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    runCheck();

    return () => {
      active = false;
      axios.interceptors.response.eject(interceptorId);
      window.clearInterval(intervalId);
      window.removeEventListener("focus", runCheck);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      redirectLockRef.current = false;
    };
  }, [location.pathname, location.search, navigate, role]);

  if (isCheckingAccess) {
    return null;
  }

  return children;
}

const RoleGate = ({ allow = [], children }) => {
  const identity = getStoredIdentity();
  const role = normalizeRole(identity.role);

  if (!identity.userid || !role) {
    return <Navigate to="/login" replace />;
  }

  if (allow.includes(role)) {
    return <RoleGateContent role={role}>{children}</RoleGateContent>;
  }

  return <Navigate to={roleHome[role] || "/login"} replace />;
};

export default RoleGate;
